import { Button, Textarea, TextInput } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import { useCurrentProject } from '@/shared/projects/project-path';
import { SelectFileInput } from '@/widgets/select-file-input';

import {
  useCreatePublisherMutation,
  useUploadPublisherPreviewMutation,
} from '../model/create-publisher-page.cases';
import {
  CREATE_PUBLISHER_SCHEMA,
  KITABA_CREATE_PUBLISHER_SCHEMA,
} from '../model/create-publisher-page.schemas';
import styles from './create-publisher-page.module.scss';

export const CreatePublisherPage = () => {
  const project = useCurrentProject();
  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      url: '',
      previewFile: null as FileWithPath | null,
    },
    validate: zodResolver(
      project === 'kitaba'
        ? KITABA_CREATE_PUBLISHER_SCHEMA
        : CREATE_PUBLISHER_SCHEMA,
    ),
  });
  const createPublisherMutation = useCreatePublisherMutation();
  const uploadPublisherPreviewMutation = useUploadPublisherPreviewMutation();
  useDirtyForm(form.isDirty());

  async function handleSubmit(values: {
    title: string;
    description: string | null;
    url: string | null;
    previewFile: FileWithPath | null;
  }) {
    if (project === 'hatfa' && !values.previewFile) return;

    const imageUrl = values.previewFile
      ? await uploadPublisherPreviewMutation
          .mutateAsync(values.previewFile)
          .catch((e) => {
            notifications.show({
              message: 'Не удалось загрузить изображение',
              color: 'red',
            });
            throw e;
          })
      : '';

    createPublisherMutation
      .mutateAsync({
        title: values.title,
        description: values.description,
        url: values.url || '',
        imgUrl: imageUrl,
      })
      .then((result) => {
        history.back();
        notifications.show({
          message: 'Издательство создано',
          color: 'green',
        });

        return result;
      })
      .catch(() => {
        notifications.show({
          message: 'Не удалось создать издательство',
          color: 'red',
        });
      });
  }

  return (
    <form className={styles.container} onSubmit={form.onSubmit(handleSubmit)}>
      <p className={styles.title}>Создание издательства</p>
      <TextInput
        placeholder="Название издательства"
        label="Название"
        className={styles.nameInput}
        {...form.getInputProps('title')}
      />
      <Textarea
        placeholder="Описание издательства"
        label="Описание"
        className={styles.input}
        {...form.getInputProps('description')}
      />
      <TextInput
        placeholder="Вставьте ссылку"
        label="Ссылка"
        className={styles.input}
        {...form.getInputProps('url')}
      />
      <SelectFileInput
        onDrop={(files) => form.setFieldValue('previewFile', files[0])}
        label="Обложка"
        description={`Допустимые форматы файлов: png
          Размер файла не должен превышать 400 кб`}
        className={styles.previewZone}
        {...form.getInputProps('imgUrl')}
      />
      {form.values.previewFile && (
        <img
          className={styles.preview}
          src={URL.createObjectURL(form.values.previewFile)}
          alt="preview"
        />
      )}
      <div className={styles.actions}>
        <Button type="submit">Сохранить</Button>
        <Button variant="light" onClick={() => history.back()}>
          Отменить
        </Button>
      </div>
    </form>
  );
};
